/** Barrel + shelf/graph/corpus/translation APIs. Design & artifacts live in submodules. */

export * from './constants'
export * from './types-design'
export * from './jobs'
export * from './design'
export * from './artifacts'

import { request } from '@/shared/api/request'
import { isL1Error } from '@/shared/api/l1-error'
import type { EducationLevel } from './constants'

export interface TeachingMajor {
  major_key: string
  name: string
  group_id?: number | null
  major_category?: string | null
  major_class?: string | null
}

export interface MajorCatalogMajor {
  major_key: string
  name: string
}

export interface MajorCatalogClass {
  name: string
  majors: MajorCatalogMajor[]
}

export interface MajorCatalogCategory {
  name: string
  classes: MajorCatalogClass[]
}

export interface MajorCatalogTree {
  categories: MajorCatalogCategory[]
}

export interface TeachingGraphNode {
  id?: string
  name?: string
  layer?: number
  description?: string
  summary?: string
  summary_enriched?: boolean
  children?: TeachingGraphNode[]
  [key: string]: unknown
}

export interface TeachingGraphData {
  graph: TeachingGraphNode | null
  template_revision: number | string
  etag?: string
  is_fork?: boolean
}

export function getMajors(educationLevel: EducationLevel): Promise<TeachingMajor[]> {
  return request.get('/teaching/majors', {
    params: { education_level: educationLevel },
  })
}

export function getMajorCatalog(educationLevel: EducationLevel): Promise<MajorCatalogTree> {
  return request.get('/teaching/major-catalog', {
    params: { education_level: educationLevel },
  })
}

export type MajorStandardGroupStatus = 'active' | 'retired'

export interface MajorStandardGroup {
  id: number
  education_level: EducationLevel | string
  major_category: string
  major_class: string
  name?: string | null
  status: MajorStandardGroupStatus | string
  display_name: string
}

export function listMajorStandardGroups(params?: {
  education_level?: EducationLevel | string
  status?: MajorStandardGroupStatus | string | null
}): Promise<MajorStandardGroup[]> {
  return request.get('/teaching/major-standard-groups', { params })
}

export function createMajorStandardGroup(body: {
  education_level: EducationLevel
  major_category: string
  major_class: string
  name?: string | null
}): Promise<MajorStandardGroup> {
  return request.post('/teaching/major-standard-groups', body)
}

export function patchMajorStandardGroup(
  groupId: number,
  body: {
    major_category?: string
    major_class?: string
    name?: string | null
    status?: MajorStandardGroupStatus
  },
): Promise<MajorStandardGroup> {
  return request.patch(`/teaching/major-standard-groups/${groupId}`, body)
}

export function getGraphs(
  educationLevel: EducationLevel,
  majorKey: string,
): Promise<TeachingGraphData> {
  return request.get('/teaching/graphs', {
    params: { education_level: educationLevel, major_key: majorKey },
  })
}

export interface GraphPutBody {
  education_level: EducationLevel | string
  major_key: string
  etag: string
  graph: TeachingGraphNode
}

export interface GraphPutResult {
  graph: TeachingGraphNode
  etag: string
  template_revision?: number | string
  is_fork?: boolean
}

export function putGraphs(body: GraphPutBody): Promise<GraphPutResult> {
  return request.put('/teaching/graphs', body)
}

export function restoreGraphs(body: {
  education_level: EducationLevel | string
  major_key: string
  confirm: boolean
}): Promise<{ restored: boolean }> {
  return request.post('/teaching/graphs/restore', body)
}

export interface GraphEnrichNodeDetailsResult {
  description: string
  cached?: boolean
  skipped?: boolean
  source?: string
  persisted?: boolean
  error?: string
}

export function enrichNodeDetails(body: {
  education_level: EducationLevel | string
  major_key: string
  node_id: string
}): Promise<GraphEnrichNodeDetailsResult> {
  return request.post('/teaching/graphs/enrich-node-details', body)
}

export interface GraphJob {
  id: number
  education_level: string
  major_key: string
  group_id?: number | null
  status: string
  graph_payload?: TeachingGraphNode | null
  qc_report?: Record<string, unknown> | null
  graph_prepare_version?: number
  error_message?: string | null
}

export function createGraphJob(body: {
  education_level: EducationLevel
  major_key: string
  group_id?: number | null
}): Promise<GraphJob> {
  return request.post('/teaching/graph-jobs', body)
}

export function listGraphJobs(): Promise<GraphJob[]> {
  return request.get('/teaching/graph-jobs')
}

export function getGraphJob(jobId: number): Promise<GraphJob> {
  return request.get(`/teaching/graph-jobs/${jobId}`)
}

export function patchGraphJob(
  jobId: number,
  body: {
    status?: string
    graph_payload?: TeachingGraphNode
    graph_prepare_version?: number
    qc_report?: Record<string, unknown>
  },
): Promise<GraphJob> {
  return request.patch(`/teaching/graph-jobs/${jobId}`, body)
}

export function publishGraphTemplate(body: {
  job_id: number
  confirm: boolean
}): Promise<{
  education_level: string
  major_key: string
  revision: number
  etag: string
}> {
  return request.post('/teaching/graph-templates/publish', body)
}

export interface AssignReviewAccepted {
  job_id?: number | null
  task_id?: number | null
  skipped?: boolean
}

export type TranslateJobStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'

export interface TeachingTranslateJob {
  job_id: number
  status: TranslateJobStatus | string
  target_language?: string
  done_segments?: number
  total_segments?: number
  error_code?: number | string
  error_message?: string
  task_id?: number | null
}

export class TranslateJobFailedError extends Error {
  readonly job: TeachingTranslateJob

  constructor(job: TeachingTranslateJob) {
    super(job.error_message || `translate failed (${job.error_code ?? job.status})`)
    this.name = 'TranslateJobFailedError'
    this.job = job
  }
}

export interface TranslationNode {
  node_key: string
  source_payload?: Record<string, unknown>
  original_target_payload?: Record<string, unknown>
  target_payload?: Record<string, unknown>
  is_modified?: boolean
  translate_status?: string
}

export interface TeachingTranslation {
  target_language: string
  status?: string
  source_revision?: number
  nodes: TranslationNode[]
}

export interface HistoryLanguage {
  code: string
  kind: string
}

export interface TeachingHistory {
  lang: string
  languages: HistoryLanguage[]
  nodes: Array<Record<string, unknown>>
  source_editable?: boolean
}

export async function pollTranslateJob(
  fetchJob: () => Promise<TeachingTranslateJob>,
  options?: {
    intervalMs?: number
    onStatus?: (job: TeachingTranslateJob) => void
  },
): Promise<TeachingTranslateJob> {
  const interval = options?.intervalMs ?? 1500
  for (;;) {
    const job = await fetchJob()
    options?.onStatus?.(job)
    if (job.status === 'completed') return job
    if (job.status === 'failed' || job.status === 'cancelled') {
      throw new TranslateJobFailedError(job)
    }
    if (interval > 0) {
      await new Promise((resolve) => setTimeout(resolve, interval))
    }
  }
}

import { workItemPath } from './paths'

export function assignReviewTask(
  workItemId: number,
  body: { assignee_id: number },
): Promise<AssignReviewAccepted> {
  return request.post(workItemPath(workItemId, '/assign-review'), body)
}

export function getTranslateJob(
  workItemId: number,
  jobId: number,
): Promise<TeachingTranslateJob> {
  return request.get(workItemPath(workItemId, `/translate-jobs/${jobId}`))
}

export function listTranslations(
  workItemId: number,
): Promise<{ versions: Array<{ target_language: string; status: string }> }> {
  return request.get(workItemPath(workItemId, '/translations'))
}

export function getTranslation(
  workItemId: number,
  lang: string,
): Promise<TeachingTranslation> {
  return request.get(workItemPath(workItemId, `/translations/${lang}`))
}

export function getTranslationNode(
  workItemId: number,
  lang: string,
  nodeKey: string,
): Promise<TranslationNode> {
  return request.get(
    workItemPath(workItemId, `/translations/${lang}/nodes/${encodeURIComponent(nodeKey)}`),
  )
}

export function patchTranslationNode(
  workItemId: number,
  lang: string,
  nodeKey: string,
  targetPayload: Record<string, unknown>,
): Promise<TranslationNode> {
  return request.patch(
    workItemPath(workItemId, `/translations/${lang}/nodes/${encodeURIComponent(nodeKey)}`),
    { target_payload: targetPayload },
  )
}

/** Design-context 课头四键（目的语 VALUES；校验只读）。 */
export interface LessonReviewDesignContext {
  course_name: string
  major_name: string
  course_direction: string
  course_description: string
}

export interface TaskLessonReview {
  task_id: number
  work_item_id: number
  target_language: string
  status?: string
  source_revision?: number
  task_status?: string
  can_edit: boolean
  lesson_plan_status?: string | null
  design_context: LessonReviewDesignContext
  /** 七段及 stage6.practice.* 目的语 nodes。 */
  nodes: TranslationNode[]
}

export function getTaskLessonReview(taskId: number): Promise<TaskLessonReview> {
  return request.get(`/teaching/tasks/${taskId}/lesson-review`)
}

export function patchTaskLessonReviewNode(
  taskId: number,
  nodeKey: string,
  targetPayload: Record<string, unknown>,
): Promise<TranslationNode> {
  return request.patch(
    `/teaching/tasks/${taskId}/lesson-review/nodes/${encodeURIComponent(nodeKey)}`,
    { target_payload: targetPayload },
  )
}

/** @deprecated Use getTaskLessonReview. */
export function getTaskLessonTranslation(..._args: [taskId: number]): never {
  void _args
  throw new Error('lesson-translation retired; use getTaskLessonReview')
}

/** @deprecated Use patchTaskLessonReviewNode. */
export function patchTaskLessonTranslationNode(
  taskId: number,
  nodeKey: string,
  targetPayload: Record<string, unknown>,
): Promise<TranslationNode> {
  return patchTaskLessonReviewNode(taskId, nodeKey, targetPayload)
}

export interface TaskLessonPlan {
  task_id: number
  work_item_id: number
  target_language: string
  status: string
  payload: Record<string, unknown>
  updated_at?: string | null
}

export function generateTaskLessonPlan(taskId: number): Promise<TaskLessonPlan> {
  return request.post(`/teaching/tasks/${taskId}/lesson-plan`)
}

export function getTaskLessonPlan(taskId: number): Promise<TaskLessonPlan> {
  return request.get(`/teaching/tasks/${taskId}/lesson-plan`)
}

/** Missing plan is normal before first generate — no toast. */
export async function getTaskLessonPlanOptional(
  taskId: number,
): Promise<TaskLessonPlan | null> {
  try {
    return await request.get(`/teaching/tasks/${taskId}/lesson-plan`, {
      skipErrorToast: true,
    })
  } catch (err) {
    if (isL1Error(err) && err.code === 4040) return null
    throw err
  }
}

export function getHistory(workItemId: number, lang?: string): Promise<TeachingHistory> {
  return request.get(workItemPath(workItemId, '/history'), {
    params: lang ? { lang } : undefined,
  })
}

export type CorpusBucket = 'pedagogy' | 'major_standard'

export interface TeachingCorpusFile {
  id: number
  bucket: CorpusBucket | string
  education_level?: string | null
  major_key?: string | null
  group_id?: number | null
  major_category?: string | null
  major_class?: string | null
  group_display_name?: string | null
  major_name?: string | null
  file_storage_id: number
  original_name?: string | null
  content_hash?: string | null
  status: string
  uploader_id: number
}

export interface TeachingRagJob {
  id: number
  job_type: 'full_rebuild' | 'incremental' | string
  status: string
  corpus_file_id?: number | null
  operator_id: number
  stats?: Record<string, unknown> | null
  error_code?: string | null
  error_message?: string | null
}

export function listCorpusFiles(params?: {
  bucket?: CorpusBucket | string
  education_level?: string
  major_key?: string
  group_id?: number
  status?: string
}): Promise<TeachingCorpusFile[]> {
  return request.get('/teaching/corpus/files', { params })
}

export function createCorpusFile(form: FormData): Promise<TeachingCorpusFile> {
  return request.post('/teaching/corpus/files', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export function replaceCorpusFile(fileId: number, form: FormData): Promise<TeachingCorpusFile> {
  return request.post(`/teaching/corpus/files/${fileId}/replace`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export function deleteCorpusFile(fileId: number): Promise<{ ok: boolean }> {
  return request.delete(`/teaching/corpus/files/${fileId}`)
}

export function retireCorpusFile(fileId: number): Promise<TeachingCorpusFile> {
  return request.patch(`/teaching/corpus/files/${fileId}`, { status: 'retired' })
}

export function createRagJob(body: {
  job_type: 'full_rebuild' | 'incremental'
  bucket: CorpusBucket
  education_level?: EducationLevel
  major_key?: string
  group_id?: number
  confirm?: boolean
}): Promise<TeachingRagJob> {
  return request.post('/teaching/rag-jobs', body)
}

export function getRagJob(jobId: number): Promise<TeachingRagJob> {
  return request.get(`/teaching/rag-jobs/${jobId}`)
}
